import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { API_URL } from 'constant';
import { loginState } from 'global-stores';
import { useNetwork } from 'hooks';

import {
	Catalog,
	ISubscriptionService,
	IBankMethod,
	ICatalogStep,
	IWireMethod,
	catalogRates,
	catalogueServices,
	heldSubscriptions,
	hasAnyMethod as computeHasAnyMethod,
	isCardExpired,
	monthlyTotal,
	resolveDefault,
	meteredLines,
	monthToDateTotal,
} from './derive';
/* THE TRANSFER SHAPES NOW BELONG TO THE eVAULT DECK, which owns the list
   itself (`views/evault/deck/transfers-deck`). This page keeps only the ONE
   figure it needs — this cycle's transfer total — because the invoice folds it
   into month-to-date and the metered card. */
import type { ITransferTotals } from 'views/evault/deck';

/**
 * EVERYTHING THE BILLING DECK READS, IN ONE PLACE.
 *
 * The deck fetches nothing the old page did not — the endpoints are the same,
 * they are simply gathered here instead of being spread across eight
 * components that each fetched for themselves.
 *
 * Two of them were mis-attributed in the original brief and are worth naming so
 * nobody re-derives the confusion (spec 18 §0):
 *   • transactions come from `GET /revenue?business=<id>`, NOT `/session-lists`
 *     (that is the sessions screen's endpoint and appears nowhere in billing)
 *   • the money model — cycle, total, per-service usage — is
 *     `GET /onboarding-billings`, NOT `/onboarding-summary` (which carries
 *     session COUNTS and feeds the consumption side, not the bill)
 */

export interface IBillingCycle {
	start?: number;
	end?: number;
	billingDate?: number;
	lastBillingDate?: number | null;
}

/** One charged line inside `onboarding-billings.services.<group>[]`. */
export interface IServiceLine {
	name?: string;
	key?: string;
	price?: number;
	quantity?: number;
}

export interface IInvoice {
	id?: string;
	number?: string;
	created?: number;
	amount_due?: number;
	amount_paid?: number;
	total?: number;
	status?: string;
	invoice_pdf?: string;
	hosted_invoice_url?: string;
}

export interface ITransaction {
	businessName?: string;
	paymentId?: string;
	transactionId?: string;
	amount?: number;
	type?: string;
	time?: string;
	card?: string;
	status?: string;
}

export interface ICard {
	id?: string;
	brand?: string;
	last4?: string;
	exp_month?: number;
	exp_year?: number;
	default?: boolean;
}

export const useBillingDeck = () => {
	const { get } = useNetwork();
	const store = useRecoilValue(loginState) as any;

	/*
	 * THE BUSINESS COMES FROM `/users`, NOT FROM `loginState`.
	 *
	 * `loginState` is not the `/users` payload — it is a flattened projection of
	 * it, carrying `businessName` at the top level and no `business` object at
	 * all. Reading `user.business._id` off it therefore yielded undefined, so
	 * `/business-prices?businessId=` was never called, the catalogue never
	 * loaded, and the page rendered "No subscription services published" and an
	 * empty rate card while the data sat there. Both were symptoms of one
	 * missing id.
	 *
	 * `/users` is the authoritative record and is one more parallel request, so
	 * the business is taken straight from it.
	 */
	const [account, setAccount] = useState<any | null>(null);
	const businessId = account?.business?._id ?? '';

	const [loading, setLoading] = useState(true);
	const [billing, setBilling] = useState<any | null>(null);
	const [catalog, setCatalog] = useState<Catalog | null>(null);
	const [subs, setSubs] = useState<Record<string, any> | null>(null);
	const [invoices, setInvoices] = useState<IInvoice[]>([]);
	const [transactions, setTransactions] = useState<ITransaction[]>([]);
	const [cards, setCards] = useState<ICard[]>([]);
	const [subAccounts, setSubAccounts] = useState<any[]>([]);
	const [steps, setSteps] = useState<ICatalogStep[] | null>(null);
	/* The Didit rate card — 45 countries, priced by pipelines.api at cost+margin.
	   Cost never reaches this client; the server does the markup. */
	const [dvRates, setDvRates] = useState<any | null>(null);
	const [wire, setWire] = useState<IWireMethod | null>(null);
	/* `banks` was discarded — only cards/wire/default were kept — which is why
	   an ACH-only account looked like it had no payment method at all. */
	const [banks, setBanks] = useState<IBankMethod[]>([]);
	const [defaultRaw, setDefaultRaw] = useState<{
		method?: string | null;
		detail?: unknown;
	} | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		const one = (url: string) =>
			get(url)
				.then((r: any) => r?.data ?? r)
				.catch(() => null);

		/* `/users` first: everything business-scoped needs its id. */
		const me = await one('/users');
		const self = Array.isArray(me) ? me[0] ?? null : me ?? null;
		setAccount(self);
		const bid = self?.business?._id ?? '';

		const [bill, prices, subscription, inv, rev, card, sub, catalogue, dv] =
			await Promise.all([
			one(`${API_URL.BILLING}?cycles=current`),
			bid ? one(`/business-prices?businessId=${bid}`) : null,
			one(API_URL.SUBSCRIPTIONS),
			one(API_URL.INVOICE),
			bid ? one(`${API_URL.BILLING_TRANSACTION}?business=${bid}`) : null,
			one('/payment-methods'),
			one(API_URL.SUB_ACCOUNT),
			/* The flow builder's own catalogue, business-resolved — the rate card's
			   only source of truth. See the note on `catalogRates`. */
			one('/pipeline-init'),
			one('/data-validation-rates'),
		]);

		setBilling(Array.isArray(bill) ? bill[0] ?? null : bill ?? null);
		setCatalog(Array.isArray(prices) ? prices[0] ?? null : prices ?? null);
		/* `/subscriptions` answers `[{entity: {status, price}}]` — one object in a
		   one-element array, keyed by service. Flattened so callers index it by
		   service name rather than remembering the wrapper. */
		setSubs(Array.isArray(subscription) ? subscription[0] ?? null : subscription ?? null);
		setInvoices(Array.isArray(inv) ? inv : []);
		setTransactions(Array.isArray(rev) ? rev : []);
		/* `/payment-methods` is the READ endpoint — `/business-cards` only ever
		   returns the cards and is write-only in the old page. This one carries
		   banks, the wire instructions and which method is actually default, and
		   this account HAS wire details, which a cards-only read would hide. */
		const pm = Array.isArray(card) ? card[0] ?? {} : card ?? {};
		setCards(Array.isArray(pm?.cards) ? pm.cards : []);
		setBanks(Array.isArray(pm?.banks) ? pm.banks : []);
		setWire(pm?.wire ?? null);
		setDefaultRaw(pm?.default ?? null);
		setSubAccounts(Array.isArray(sub) ? sub : []);
		setDvRates(Array.isArray(dv) ? dv[0] ?? null : dv ?? null);
		setSteps(
			Array.isArray(catalogue)
				? (catalogue as ICatalogStep[])
				: (catalogue as any)?.steps ?? null
		);
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* One narrow refetch for every payment mutation — the band, the ledger leg,
	   the rail vignette and the drawer all read the same state, so they heal
	   together instead of four separate reloads. */
	const refreshPayments = useCallback(async () => {
		const resp: any = await get('/payment-methods').catch(() => null);
		const d = resp?.data ?? resp;
		const pm = Array.isArray(d) ? d[0] ?? {} : d ?? {};
		setCards(Array.isArray(pm?.cards) ? pm.cards : []);
		setBanks(Array.isArray(pm?.banks) ? pm.banks : []);
		setWire(pm?.wire ?? null);
		setDefaultRaw(pm?.default ?? null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* THIS CYCLE'S transfer totals, folded into `meteredTotal` (and so into the
	   card ONLY. Always `period=current` — the list that used to share this
	   hook, with a picker that could point at a past month, now lives on its own
	   page with its own fetch (`useTransferCharges`), so browsing history there
	   cannot move a figure the invoice has to agree with. */
	const [currentTransferTotals, setCurrentTransferTotals] =
		useState<ITransferTotals>({ quantity: 0, amount: 0 });

	const loadCurrentTransferTotals = useCallback(async () => {
		const resp: any = await get(
			`${API_URL.EVAULT_TRANSFER_CHARGES}?period=current`
		).catch(() => null);
		const d = resp?.data ?? resp;
		setCurrentTransferTotals(d?.totals ?? { quantity: 0, amount: 0 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* `loadCurrentTransferTotals` carries no period of its own — it is always
	   "now" — so it needs no dep beyond its own stable identity. */
	const refreshAll = useCallback(() => {
		load();
		loadCurrentTransferTotals();
	}, [load, loadCurrentTransferTotals]);

	useEffect(() => {
		load();
		loadCurrentTransferTotals();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* ── the two pricing models, kept apart ────────────────────────────── */
	/* What the account HOLDS (from /subscriptions, keyed by account type) and
	   what the catalogue LISTS (from /business-prices, keyed by service) — two
	   different vocabularies, kept apart on purpose. */
	const held: ISubscriptionService[] = useMemo(
		() => heldSubscriptions(subs),
		[subs]
	);
	const services: ISubscriptionService[] = useMemo(
		() => catalogueServices(catalog),
		[catalog]
	);
	/* THE RATE CARD IS THE BUILDER'S CATALOGUE, NOT THE PRICE BOOK.
	   `/business-prices` is still read — the subscription services and the
	   metered-usage maths need it — but it must never be what a customer is
	   quoted, because its numbers disagree with what the flow builder shows. */
	const rates = useMemo(() => catalogRates(steps), [steps]);
	const monthly = useMemo(() => monthlyTotal(held), [held]);

	/**
	 * WHAT HAS ACTUALLY BEEN CONSUMED THIS CYCLE.
	 *
	 * `onboarding-billings.services.<group>[]` gives `{name, key, quantity,
	 * price}` — and `price` is the LINE TOTAL, not the unit rate. Verified by
	 * arithmetic against live data: signAgreement `qty 84, price 168` is
	 * 168/84 = $2.00 each; kyb `qty 62, price 310` is 310/62 = $5.00 each.
	 *
	 * This multiplied `quantity * price` on the reasonable-looking assumption
	 * that a field called `price` is a unit price, which turned 84 e-signatures
	 * worth $168 into $14,112. The name is the trap; the arithmetic is the
	 * proof. The unit rate is DERIVED for display and never used to compute a
	 * total — the server's figure is what the customer is charged.
	 */
	/* Metered = every consumed row that is NOT a recurring `*Service` line — the
	   plan is shown as the plan, once. See `meteredLines` in derive.ts. */
	const usage = useMemo(() => meteredLines(billing?.services), [billing]);

	/* + this cycle's eVault transfer total: `withAddonLine` (clients.api) already
	   folds the transfer line into the invoice's metered figure, so this card
	   has to agree with the invoice rather than under-count by one line.
	   `monthToDate` below is plan + THIS figure, so the transfer line reaches
	   the tile exactly once — through here, and nowhere else. */
	const meteredTotal = useMemo(
		() =>
			usage.reduce((sum, l) => sum + l.total, 0) +
			Number(currentTransferTotals.amount ?? 0),
		[usage, currentTransferTotals]
	);

	const cycle: IBillingCycle = useMemo(
		() => billing?.billingCycle ?? {},
		[billing]
	);
	/* Resolved once, from BOTH default signals — see `resolveDefault`. */
	const resolved = useMemo(
		() => resolveDefault(cards, banks, wire, defaultRaw ?? undefined),
		[cards, banks, wire, defaultRaw]
	);
	const defaultCard = resolved.card ?? null;
	const cardExpired = useMemo(() => isCardExpired(defaultCard), [defaultCard]);

	const daysToBilling = useMemo(() => {
		const at = cycle?.billingDate ?? cycle?.end;
		if (!at) return null;
		return Math.ceil((at - Date.now()) / 86_400_000);
	}, [cycle]);

	return {
		loading,
		refresh: refreshAll,
		businessId,
		account,
		companyName: account?.business?.name ?? store?.businessName ?? '',
		parentBusinessId: account?.business?.parentBusinessId ?? null,
		billing,
		cycle,
		daysToBilling,
		/* money */
		/* plan (+ add-ons) + metered usage + this cycle's eVault transfers — the
		   same three parts the metered card shows, from ONE computation.
		   This used to be `billing.totalAmount + monthly + transfers`. But the
		   server's `totalAmount` already includes the recurring `*Service` rows,
		   so the plan was counted there AND in `monthly`: a fresh $19.99
		   subscription read $39.98. `meteredTotal` already carries the transfer
		   line, so it is not added again here either. */
		monthToDate: monthToDateTotal(monthly, meteredTotal),
		monthly,
		meteredTotal,
		/* the vendor rate card */
		dvRates,
		/* the two pricing models */
		held,
		services,
		rates,
		usage,
		/* tables */
		invoices,
		transactions,
		subAccounts,
		/* payment */
		cards,
		banks,
		defaultCard,
		cardExpired,
		wire,
		refreshPayments,
		/* what will actually be charged, and whether anything designated it */
		defaultMethod: resolved.kind,
		/* The ACH designation names a bank AND an account; the drawer needs both
		   to chip the right row rather than every bank. */
		defaultBankId: resolved.bankId ?? null,
		defaultAccountId: resolved.accountId ?? null,
		defaultIsDesignated: resolved.designated,
		hasAnyMethod: computeHasAnyMethod(cards, banks, defaultRaw ?? undefined),
	};
};
